'use client';
import React from 'react';

import Highlighter from 'react-highlight-words';

// import EditInAdminButton from '../EditInAdminButton';
import EmployeeActions from './EmployeeActions';
import { ArticleItemWrap, ArticleIconWrap, Content, Title, Description } from './SearchResultItem.styled';
import { IconName } from '@/ui/components/Button/Icons';
import Icon from '@/ui/components/Icon';
import Link from '@/ui/components/Link';
import { descriptionCute } from '@/utils/helpers';
import { log } from '@/utils/logger';
import { normalize } from '@/utils/normalize';

/**
 * @todo adjust for compact prop - two vriants of the component
 * @todo admin edit button
 */
interface ArticleItemProps {
  href?: string;
  imgSrc?: string;
  imgAlt?: string;
  title: string;
  description?: string;
  buttonLabel?: string;
  buttonIcon?: IconName;
  compact?: boolean;
  noBorder?: boolean;
  type?: 'text' | 'video';
  videoLink?: string;
  item?: RestDataPayload;
  searchExpression: string;
}

// Compiled regex patterns for reuse
const HTML_TAGS_REGEX = /<[^>]*>/g;
const PUNCTUATION_REGEX = /[.,/#!$%^&*;:{}=\-_`~()]/g;
const WORD_BOUNDARY_REGEX = /\w/;

const CZECH_SUFFIXES = ['it', 'ení', 'ání', 'y', 'a', 'e', 'u', 'ů', 'ích', 'ovat', 'ování', 'ová', 'ovati'];

const STEMMING_PATTERNS = [/(it|ení|ání|y|a|e|u|ů|ích|ích|ích|ích)$/i, /(ovat|ování|ová|vati)$/i];

// Function to stem Czech words (simplified)
const stemCzech = (word: string) => {
  return word
    .replace(STEMMING_PATTERNS[0], '') // Basic stemming
    .replace(STEMMING_PATTERNS[1], 'ov'); // Handle verb forms
};

// Function to generate all variants of a stemmed word
const generateVariants = (stemmedWord: string) => {
  return [stemmedWord, ...CZECH_SUFFIXES.map(suffix => stemmedWord + suffix)];
};

// Custom function for partial word matching
const customFindChunks = ({
  searchWords,
  textToHighlight,
}: {
  searchWords: string[];
  textToHighlight: string;
}): { start: number; end: number }[] => {
  if (!Array.isArray(searchWords) || typeof textToHighlight !== 'string') {
    throw new Error('Invalid input: searchWords must be an array and textToHighlight must be a string.');
  }

  const normalizedText = normalize(textToHighlight || '').toLowerCase();
  if (!normalizedText) return [];

  return searchWords.flatMap(word => {
    if (typeof word !== 'string' || !word.trim()) return [];

    try {
      const normalizedWord = normalize(word).toLowerCase();
      const stemmedWord = stemCzech(normalizedWord);
      const variants = generateVariants(stemmedWord);

      if (!Array.isArray(variants) || variants.length === 0) return [];

      const matches: { start: number; end: number }[] = [];

      for (const variant of variants) {
        if (!variant) continue;

        let startIndex = 0;
        while ((startIndex = normalizedText.indexOf(variant, startIndex)) !== -1) {
          // Ensure word boundaries
          let wordStart = startIndex;
          while (wordStart > 0 && WORD_BOUNDARY_REGEX.test(normalizedText[wordStart - 1])) {
            wordStart--;
          }
          let wordEnd = startIndex + variant.length;
          while (wordEnd < normalizedText.length && WORD_BOUNDARY_REGEX.test(normalizedText[wordEnd])) {
            wordEnd++;
          }

          // Avoid duplicate entries
          if (!matches.some(match => match.start === wordStart && match.end === wordEnd)) {
            matches.push({ start: wordStart, end: wordEnd });
          }

          startIndex += variant.length;
          if (startIndex >= normalizedText.length) break; // Prevent infinite loops
        }
      }

      return matches;
    } catch (error) {
      console.error('Error processing word:', word, error);
      return [];
    }
  });
};

// const getFirstWords = (text: string) => {
//   const textWithoutTags = text.replace(/<[^>]*>/g, '');
//   const textWithoutSpecialChars = textWithoutTags.replace(/[^a-zA-Z0-9 ]/g, '');
//   const firstWords = textWithoutSpecialChars.split(' ').slice(0, 3);
//   return firstWords ? firstWords.join(' ') : '';
// };

// const linkWithScrollToText = ({
//   href,
//   text,
// }: {
//   href: string;
//   text: string | undefined;
// }) => {
//   if (!text) {
//     return href;
//   }

//   const firstWordsString = getFirstWords(text);

//   if (firstWordsString) {
//     // custom alt to #:~:tex=
//     return `${href}#text${encodeURIComponent(firstWordsString)}`;
//   }

//   return href;
// };

export default function SearchResultItem({
  href,
  title,
  description,
  noBorder = false,
  searchExpression,
}: ArticleItemProps) {
  const wordsToHighlight = React.useMemo(() => {
    const words = decodeURIComponent(searchExpression).split(' ');

    // take words longer then 4 characters and remove the last letter and add these to the list of words to highlight
    const wordsToHighlight = words.filter(word => word.length > 4).map(word => word.slice(0, -1));

    const normalizedStrings = words.map(word => word.toLowerCase());

    const withoutPunctuation = normalizedStrings.map(word => word.replace(PUNCTUATION_REGEX, ''));

    return [...words, ...wordsToHighlight, ...withoutPunctuation];
  }, [searchExpression]);

  const sanitizedTitle = React.useMemo(() => {
    // remove html tags
    const titleWithoutTags = title.replace(HTML_TAGS_REGEX, '');
    // remove &nbsp; and other special characters but keep list items
    // const titleWithoutSpecialChars = titleWithoutTags.replace(
    //   /[^a-zA-Z0-9 ]/g,
    //   ''
    // );

    return titleWithoutTags;
  }, [title]);

  const sanitizedDescription = React.useMemo(() => {
    // remove html tags
    const descriptionWithoutTags = description?.replace(HTML_TAGS_REGEX, '');
    // remove &nbsp; and other special characters but keep list items
    // const descriptionWithoutSpecialChars = descriptionWithoutTags?.replace(
    //   /[^a-zA-Z0-9 ]/g,
    //   ''
    // );

    return descriptionWithoutTags;
  }, [description]);

  const processedTitle = React.useMemo(() => {
    return sanitizedTitle.replace(' | Prusa Knowledge Base', '');
  }, [sanitizedTitle]);

  const processedDescription = React.useMemo(() => {
    return descriptionCute(sanitizedDescription);
  }, [sanitizedDescription]);

  if (!title || !href) {
    log.error('SearchResultItem: Missing title or href');
    return null;
  }

  return (
    <ArticleItemWrap compact noBorder={noBorder}>
      <EmployeeActions href={href} title={title} description={description} />

      <ArticleIconWrap>
        <Link href={href}>
          {/* linkWithScrollToText({ href, text: description }) */}
          {href.includes('pdf') ? <Icon name="file-pdf" size="2xl" /> : <Icon name="file-lines" size="2xl" />}
        </Link>
      </ArticleIconWrap>

      <Content>
        <Title variant="h5" component="h3">
          <Link href={href}>
            <Highlighter
              highlightClassName="YourHighlightClass"
              searchWords={wordsToHighlight}
              autoEscape={true}
              textToHighlight={processedTitle}
              findChunks={customFindChunks}
              highlightStyle={{ background: 'transparent' }}
            />
          </Link>
        </Title>

        <Description>
          <Highlighter
            highlightClassName="YourHighlightClass"
            searchWords={wordsToHighlight}
            autoEscape={true}
            textToHighlight={processedDescription}
            highlightStyle={{ background: 'transparent' }}
            findChunks={customFindChunks}
          />
        </Description>
      </Content>
    </ArticleItemWrap>
  );
}
